import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

function HealthyHome() {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
            setIsLoaded(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    if (!isLoaded) {
        return (
            <div className="min-h-screen bg-brand-cream flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange mx-auto mb-4"></div>
                    <p className="text-brand-green">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-brand-cream" style={{ opacity: 1 }}>
            {/* Hero Section */}
            <section className="bg-brand-green text-white py-20">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <motion.h1
                        className="text-4xl md:text-6xl font-bold mb-6"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        Healthy Home Inspection
                    </motion.h1>
                    <motion.p
                        className="text-xl md:text-2xl max-w-3xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                        Comprehensive inspections to ensure your rental property meets Healthy Home Standards
                    </motion.p>
                </div>
            </section>

            {/* Introduction Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <motion.h2
                                className="text-3xl md:text-4xl font-bold text-black mb-6"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                Meet Your Legal Requirements with Confidence
                            </motion.h2>
                            <motion.p
                                className="text-lg text-gray-700 mb-6"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                            >
                                At Mega Property Solutions, we offer comprehensive Healthy Home Inspections to help property owners meet the legal requirements under the Healthy Home Standards. Our certified team assesses key areas of your rental property, ensuring compliance with all regulations.
                            </motion.p>
                            <motion.p
                                className="text-lg text-gray-700 mb-8"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                Whether you're preparing a rental for new tenants or responding to a tenancy request, our inspection gives you peace of mind and keeps you compliant.
                            </motion.p>
                        </motion.div>
                        <motion.div
                            className="relative"
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <div className="bg-gray-200 h-96 rounded-2xl overflow-hidden shadow-lg">
                                <img
                                    src={require('../images/healthy-home.png')}
                                    alt="Healthy Home Inspection"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* What We Inspect Section */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                            What We Inspect
                        </h2>
                        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                            Our comprehensive inspection covers all five key areas required by the Healthy Home Standards
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Insulation */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-8 shadow-lg"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-black mb-4">Insulation</h3>
                            <p className="text-gray-700">
                                Ceiling and underfloor insulation checked for R-value, condition, and coverage to ensure compliance with Healthy Home Standards.
                            </p>
                        </motion.div>

                        {/* Heating */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-8 shadow-lg"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-black mb-4">Heating</h3>
                            <p className="text-gray-700">
                                Verification of an efficient fixed heating source in the main living area that meets the required heating capacity standards.
                            </p>
                        </motion.div>

                        {/* Ventilation */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-8 shadow-lg"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-black mb-4">Ventilation</h3>
                            <p className="text-gray-700">
                                Inspection of kitchens and bathrooms for appropriate extractor fans and overall airflow to maintain healthy indoor air quality.
                            </p>
                        </motion.div>

                        {/* Moisture & Drainage */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-8 shadow-lg"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-black mb-4">Moisture & Drainage</h3>
                            <p className="text-gray-700">
                                Check for moisture barriers under floors and assess drainage systems to prevent moisture-related issues.
                            </p>
                        </motion.div>

                        {/* Draught Stopping */}
                        <motion.div
                            className="bg-brand-cream rounded-2xl p-8 shadow-lg md:col-span-2 lg:col-span-1"
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.6 }}
                        >
                            <div className="bg-brand-green rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-black mb-4">Draught Stopping</h3>
                            <p className="text-gray-700">
                                Identify and report gaps or holes in walls, floors, windows, and doors that need to be sealed for energy efficiency.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* What We Offer Section */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
                            What We Offer
                        </h2>
                        <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                            Complete inspection services with detailed reporting and follow-up solutions
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <motion.div
                            className="bg-white rounded-2xl p-8 shadow-lg"
                            initial={{ opacity: 0, x: -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <div className="bg-brand-orange rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-black mb-4">Full Property Inspection Report</h3>
                            <p className="text-gray-700 mb-4">
                                Comprehensive inspection report with detailed photos documenting the current state of your property and compliance status.
                            </p>
                            <ul className="space-y-2 text-gray-700">
                                <li className="flex items-start">
                                    <span className="text-brand-orange mr-2 mt-1">•</span>
                                    <span>Detailed assessment of all five Healthy Home Standards</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-brand-orange mr-2 mt-1">•</span>
                                    <span>High-quality photos of all inspected areas</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-brand-orange mr-2 mt-1">•</span>
                                    <span>Clear compliance status for each standard</span>
                                </li>
                            </ul>
                        </motion.div>

                        <motion.div
                            className="bg-white rounded-2xl p-8 shadow-lg"
                            initial={{ opacity: 0, x: 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                        >
                            <div className="bg-brand-orange rounded-xl p-4 w-16 h-16 flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-black mb-4">Upgrade & Installation Services</h3>
                            <p className="text-gray-700 mb-4">
                                Clear outline of areas needing improvement with upgrade/installation quotes if non-compliant.
                            </p>
                            <ul className="space-y-2 text-gray-700">
                                <li className="flex items-start">
                                    <span className="text-brand-orange mr-2 mt-1">•</span>
                                    <span>Detailed quotes for insulation, moisture barriers, and other upgrades</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-brand-orange mr-2 mt-1">•</span>
                                    <span>Priority scheduling for insulation installs if required</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-brand-orange mr-2 mt-1">•</span>
                                    <span>Expert recommendations for achieving compliance</span>
                                </li>
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-brand-green text-white">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <motion.h2
                        className="text-3xl md:text-4xl font-bold mb-6"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        Ready to Ensure Your Property is Compliant?
                    </motion.h2>
                    <motion.p
                        className="text-xl mb-8 max-w-2xl mx-auto"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                    >
                        Contact us today to book your Healthy Home Inspection and get peace of mind knowing your rental property meets all legal requirements.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
                    >
                        <Link
                            to="/contact"
                            className="inline-block bg-brand-orange text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-transparent hover:text-brand-orange border-2 border-transparent hover:border-brand-orange transition-all duration-300 relative overflow-hidden group"
                        >
                            <span className="relative z-10">Book Your Inspection</span>
                            <div className="absolute inset-0 bg-brand-orange transform translate-x-0 group-hover:-translate-x-full transition-transform duration-300"></div>
                        </Link>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}

export default HealthyHome;

